/**
 * THE STATE BEHIND DOCUMENTS & DRAWINGS.
 *
 * Three things this hook exists to get right, each of which the file-manager
 * page it replaces got wrong or never attempted:
 *
 * 1. THE CONTRACT IS READ ONCE, BEFORE ANYTHING RENDERS. Every row goes
 *    through `readDocumentListContract`, so a malformed payload produces a
 *    stated "could not be read" rather than a white screen or a silent
 *    "no documents" — MALFORMED IS NOT EMPTY.
 *
 * 2. THE SLOW RESPONSE NEVER WINS. Selecting document B while document A's
 *    signed-URL refresh is still in flight must leave B on screen when A
 *    lands, and B must be on screen the whole time rather than after it.
 *
 *    ONE guard does that, and the mutation harness is how we know which:
 *    the refreshed view is cached UNDER ITS OWN DOCUMENT ID and is only ever
 *    read back for that id. A first draft also ran every detail fetch through
 *    `createSequencer`; deleting that sequencer failed no test, because the
 *    effect's own cleanup flag already discarded the superseded response.
 *    A guard whose absence nothing can detect is not defence in depth, it is
 *    a claim — so it was removed rather than left standing with a comment.
 *
 *    The LIST load keeps its sequencer, because that one is load-bearing:
 *    `loadDocuments` runs from an effect with no cleanup and is also called
 *    directly by Retry, so two loads really can overlap and the slower really
 *    can win. `documents-workspace.spec.js` pins both.
 *
 * 3. A REFUSAL IS NOT A FAILURE. `503 document_storage_not_configured` is the
 *    deployment saying object storage was never wired up. It is reported in
 *    those words, not as "Upload failed", because nothing was attempted and
 *    nothing was lost.
 *
 * The selection lives in the URL (`?doc=<id>`), so the browser back button
 * walks back through documents the way a person expects, a deep link opens
 * the drawing it names, and the mobile list→viewer flow gets sane history for
 * free rather than through a bespoke stack.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import documentsService from '../../services/documentsService';
import { createSequencer } from '../../lib/sequencer';
import { classifyWorkflowError } from '../../lib/workflowErrors';
import {
  readDocumentContract,
  readDocumentListContract,
  presentUploadFailure,
  resolveSelection,
  selectionAfterDeletion,
} from './documentPresentation';

export const useDocumentWorkspace = (
  projectId,
  { selectedId, onSelect } = {},
) => {
  const [rows, setRows] = useState([]);
  const [unreadable, setUnreadable] = useState([]);
  const [contractError, setContractError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [storageRefusal, setStorageRefusal] = useState(null);
  const [mutating, setMutating] = useState(false);

  const [detail, setDetail] = useState(null); // {id, document}
  const [detailLoading, setDetailLoading] = useState(false);

  const [focusRequestId, setFocusRequestId] = useState(null);

  const listSeq = useRef(createSequencer()).current;

  /* ---------------------------------------------------------------------
   * THE LIST
   * ------------------------------------------------------------------ */

  const loadDocuments = useCallback(async () => {
    if (!projectId) return;
    const ticket = listSeq.next();
    setLoading(true);
    try {
      const payload = await documentsService.getAll(projectId);
      if (!listSeq.isCurrent(ticket)) return;
      const read = readDocumentListContract(payload);
      if (!read.readable) {
        // The envelope is unreadable. Refusing it WHOLE is the point: an
        // unknown shape rendered as an empty list would claim this project
        // has no documents, which is a claim about the project.
        setContractError(read.reason);
        setRows([]);
        setUnreadable([]);
      } else {
        setContractError(null);
        setRows(read.documents);
        setUnreadable(read.unreadable);
      }
      setLoadError(null);
    } catch (error) {
      if (!listSeq.isCurrent(ticket)) return;
      setLoadError(classifyWorkflowError(error));
      setRows([]);
      setUnreadable([]);
    } finally {
      if (listSeq.isCurrent(ticket)) setLoading(false);
    }
  }, [projectId, listSeq]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  /* ---------------------------------------------------------------------
   * THE SELECTION
   * ------------------------------------------------------------------ */

  /** A selection only counts if the row is still in the list. */
  const resolvedId = useMemo(
    () => resolveSelection(rows, selectedId),
    [rows, selectedId],
  );
  const summary = useMemo(
    () => (resolvedId ? rows.find((d) => d.id === resolvedId) || null : null),
    [rows, resolvedId],
  );

  /**
   * The list response already carries a signed URL, so the summary renders
   * immediately and the preview never lags a click. This refresh exists
   * because the list's link is minted at 60 minutes and a single document's
   * at 120: a workspace left open past the shorter expiry would otherwise
   * show a dead preview for a perfectly good file.
   *
   * The `cancelled` flag below is React hygiene — it stops a superseded
   * effect writing state at all. It is NOT what makes the race safe: the
   * id-keyed cache read is (see `selectedDocument`), and that is the one a
   * test can prove.
   */
  useEffect(() => {
    if (!projectId || !resolvedId) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    documentsService
      .getById(projectId, resolvedId)
      .then((payload) => {
        if (cancelled) return;
        const read = readDocumentContract(payload);
        // An unreadable detail leaves the list's own row standing. The row
        // came from a readable list and is not retracted by a bad refresh.
        if (read.readable)
          setDetail({ id: resolvedId, document: read.document });
      })
      .catch(() => {
        // A failed refresh is not a failed document: the summary — including
        // its still-valid signed URL — stays exactly as it was.
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, resolvedId]);

  /**
   * THE RACE GUARD, and the only one on this path.
   *
   * A cached refresh is read back ONLY for the document it belongs to. Two
   * things follow, and both are pinned by tests:
   *
   *   a stale response cannot be rendered under another document's name; and
   *   selecting B while B's own refresh is still in flight shows B's row
   *   immediately, rather than holding A's finished view on screen until
   *   B's arrives.
   *
   * The second is the one that bites in practice — a fast switch between two
   * drawings on a slow connection — and it is why this is a keyed read rather
   * than a plain `detail || summary`.
   */
  const selectedDocument = useMemo(() => {
    if (!resolvedId) return null;
    if (detail && detail.id === resolvedId) return detail.document;
    return summary;
  }, [resolvedId, detail, summary]);

  const select = useCallback(
    (id) => {
      onSelect?.(id);
    },
    [onSelect],
  );

  /* ---------------------------------------------------------------------
   * MUTATION
   * ------------------------------------------------------------------ */

  const uploadDocument = useCallback(
    async (file, metadata = {}) => {
      setUploading(true);
      setUploadProgress(0);
      setStorageRefusal(null);
      try {
        const payload = await documentsService.upload(
          projectId,
          file,
          metadata,
          setUploadProgress,
        );
        const read = readDocumentContract(payload);
        if (read.readable) {
          setRows((prev) => [
            read.document,
            ...prev.filter((d) => d.id !== read.document.id),
          ]);
          toast.success(`${file.name} uploaded`);
          select(read.document.id);
        } else {
          // The upload succeeded on the wire; the response could not be read.
          // Reloading asks the backend rather than inventing a row from a
          // payload we just refused.
          await loadDocuments();
          toast.success(`${file.name} uploaded`);
        }
        return { success: true };
      } catch (error) {
        const classified = classifyWorkflowError(error);
        const presented = presentUploadFailure(
          classified,
          error?.response?.data?.detail,
        );
        if (presented.kind === 'STORAGE_NOT_CONFIGURED') {
          // An operational condition of the environment, shown as a standing
          // banner rather than a toast that disappears in four seconds.
          setStorageRefusal(presented);
        } else {
          toast.error(presented.detail);
        }
        return { success: false, error: presented };
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [projectId, select, loadDocuments],
  );

  const updateDocument = useCallback(
    async (doc, updates) => {
      setMutating(true);
      try {
        const payload = await documentsService.update(
          projectId,
          doc.id,
          updates,
        );
        const read = readDocumentContract(payload);
        if (read.readable) {
          setRows((prev) =>
            prev.map((d) => (d.id === doc.id ? read.document : d)),
          );
          /*
            PUT returns the row with NO download_url — the backend does not
            mint one on update. Merging it into the detail cache would strip a
            working signed URL and turn a previewable drawing into
            "link not issued". The cache is dropped instead, and the next
            refresh re-establishes it from the source that owns it.
          */
          setDetail((prev) => (prev && prev.id === doc.id ? null : prev));
        }
        toast.success('Document updated');
        return { success: true };
      } catch (error) {
        const classified = classifyWorkflowError(error);
        toast.error(classified.message);
        return { success: false, error: classified };
      } finally {
        setMutating(false);
      }
    },
    [projectId],
  );

  const deleteDocument = useCallback(
    async (doc) => {
      setMutating(true);
      try {
        await documentsService.delete(projectId, doc.id);
        /*
          Where focus goes is decided from the list AS IT WAS, before the row
          left it — the row that takes the deleted row's place is the nearest
          thing to "where you were". Focus that lands nowhere sends a keyboard
          user back to the top of the document, past the entire rail.
        */
        const nextId = selectionAfterDeletion(rows, doc.id);
        setRows((prev) => prev.filter((d) => d.id !== doc.id));
        setDetail((prev) => (prev && prev.id === doc.id ? null : prev));
        select(nextId);
        setFocusRequestId(nextId || '__list__');
        toast.success('Document deleted');
        return { success: true };
      } catch (error) {
        const classified = classifyWorkflowError(error);
        toast.error(classified.message);
        return { success: false, error: classified };
      } finally {
        setMutating(false);
      }
    },
    [projectId, rows, select],
  );

  /**
   * Stable identities: the page focuses a row inside an effect keyed on these,
   * and a fresh closure every render would re-run that effect forever.
   */
  const clearFocusRequest = useCallback(() => setFocusRequestId(null), []);
  const dismissStorageRefusal = useCallback(() => setStorageRefusal(null), []);

  const downloadDocument = useCallback((doc) => {
    // The backend's own temporary URL, opened as-is. Nothing here builds one.
    if (doc?.downloadUrl) {
      documentsService.download(doc.downloadUrl);
      return;
    }
    toast.error(
      'No download link was issued for this file. The record still exists.',
    );
  }, []);

  return {
    documents: rows,
    unreadable,
    contractError,
    loadError,
    loading,
    reload: loadDocuments,

    selectedId: resolvedId,
    selectedDocument,
    detailLoading: detailLoading && !summary,
    select,

    uploading,
    uploadProgress,
    storageRefusal,
    dismissStorageRefusal,
    mutating,
    uploadDocument,
    updateDocument,
    deleteDocument,
    downloadDocument,

    focusRequestId,
    clearFocusRequest,
  };
};

export default useDocumentWorkspace;
